import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, AlertCircle, ArrowLeft, BookOpen, Calendar, ExternalLink } from 'lucide-react';
import { blogApi } from '../../api';
import { Card, CardContent } from '../../components/ui/card';

/** Minimal & safe markdown renderer — supports H2/H3, paragraphs, bold, italic, links,
 *  unordered/ordered lists, and code blocks. Escapes everything else.
 *  We deliberately keep this self-contained (no DOMPurify dep) by escaping HTML
 *  first, then re-introducing only an allow-list of inline patterns.
 */
function renderMarkdown(md) {
  if (!md) return '';
  // 1. escape raw HTML
  let s = md
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // 2. code blocks ```
  s = s.replace(/```([\s\S]*?)```/g, (_, code) =>
    `<pre class="bg-slate-100 dark:bg-slate-800 rounded-lg p-3 overflow-x-auto text-xs my-4"><code>${code.trim()}</code></pre>`,
  );

  // 3. headings (process longest first)
  s = s.replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold mt-6 mb-2">$1</h3>');
  s = s.replace(/^## (.+)$/gm, '<h2 class="text-2xl font-bold mt-8 mb-3">$1</h2>');
  s = s.replace(/^# (.+)$/gm, '<h2 class="text-2xl font-bold mt-8 mb-3">$1</h2>');

  // 4. unordered + ordered lists (very loose)
  s = s.replace(/^(?:- |\* )(.+)$/gm, '<li>$1</li>');
  s = s.replace(/(<li>.+?<\/li>(?:\n<li>.+?<\/li>)*)/g, (m) =>
    `<ul class="list-disc pl-6 my-3 space-y-1">${m.replace(/\n/g, '')}</ul>`,
  );
  s = s.replace(/^\d+\.\s(.+)$/gm, '<oli>$1</oli>');
  s = s.replace(/(<oli>.+?<\/oli>(?:\n<oli>.+?<\/oli>)*)/g, (m) =>
    `<ol class="list-decimal pl-6 my-3 space-y-1">${m.replace(/<oli>/g, '<li>').replace(/<\/oli>/g, '</li>').replace(/\n/g, '')}</ol>`,
  );

  // 5. bold + italic + inline code
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  s = s.replace(/`([^`\n]+)`/g, '<code class="bg-slate-100 dark:bg-slate-800 rounded px-1 py-0.5 text-xs">$1</code>');

  // 6. links [text](url)
  s = s.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">$1</a>',
  );

  // 7. paragraphs — split on blank lines and wrap non-block lines
  const blocks = s.split(/\n{2,}/);
  return blocks
    .map((block) => {
      const trimmed = block.trim();
      if (!trimmed) return '';
      if (/^<(h2|h3|ul|ol|pre|blockquote)/i.test(trimmed)) return trimmed;
      return `<p class="my-3 leading-relaxed text-[15px]">${trimmed.replace(/\n/g, '<br/>')}</p>`;
    })
    .join('\n');
}

export default function BlogDetail() {
  const { slug, blogSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const { data: resp } = await blogApi.publicDetail(slug, blogSlug);
      setData(resp);
    } catch (e) {
      setError(e.response?.data?.detail || 'Blog post not found');
    } finally {
      setLoading(false);
    }
  }, [slug, blogSlug]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (data?.blog?.title) {
      const brand = data?.branding?.app_name || 'CredBloom';
      document.title = `${data.blog.title} · ${brand}`;
    }
  }, [data]);

  const renderedHtml = useMemo(() => renderMarkdown(data?.blog?.content || ''), [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-primary/10">
        <Loader2 size={28} className="animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-3">
            <AlertCircle size={36} className="text-red-500 mx-auto" />
            <h2 className="text-lg font-semibold">Blog post not found</h2>
            <p className="text-sm text-muted-foreground">{error}</p>
            <Link to={`/blog/${slug}`} className="text-primary hover:underline text-sm inline-block pt-2">
              ← Back to blog
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const branding = data?.branding || { app_name: 'CredBloom', primary_color: null, logo_url: null };
  const blog = data?.blog;
  const websiteUrl = data?.website_url;
  const brandColor = branding.primary_color;
  const tileStyle = brandColor ? { backgroundColor: brandColor, color: '#fff' } : undefined;

  return (
    <div className="min-h-screen px-4 py-8 bg-gradient-to-br from-primary/5 via-background to-primary/10">
      <article className="max-w-3xl mx-auto" data-testid="blog-article">
        {/* Brand + back link */}
        <div className="flex items-center justify-between mb-6">
          <Link
            to={`/blog/${slug}`}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            data-testid="back-to-list"
          >
            <ArrowLeft size={14} /> Back to {data?.page_name || 'blog'}
          </Link>
          <div className="flex items-center gap-2">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.app_name} className="w-8 h-8 rounded-lg object-cover" />
            ) : (
              <div
                className="w-8 h-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center"
                style={tileStyle}
              >
                <BookOpen size={16} />
              </div>
            )}
          </div>
        </div>

        {/* Hero image */}
        {blog?.image_url && (
          <img
            src={blog.image_url}
            alt={blog.title}
            className="w-full max-h-96 object-cover rounded-2xl mb-6 shadow-sm"
          />
        )}

        {/* Title + date */}
        <header className="mb-6 space-y-3">
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight tracking-tight" style={{ fontFamily: 'Manrope' }}>
            {blog?.title}
          </h1>
          {blog?.published_at && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Calendar size={14} />
              {new Date(blog.published_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
          )}
        </header>

        {/* Body */}
        <div
          className="text-foreground/90"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: renderedHtml }}
          data-testid="blog-content"
        />

        {/* Contact / Visit Us CTA */}
        {websiteUrl && (
          <div
            className="mt-10 p-6 rounded-2xl border border-border bg-gradient-to-br from-primary/5 via-background to-primary/10 text-center"
            data-testid="blog-website-cta"
          >
            <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Manrope' }}>
              Want to know more?
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Visit our website to get in touch.
            </p>
            <a
              href={websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold text-white shadow-sm hover:shadow-md transition-shadow"
              style={brandColor ? { backgroundColor: brandColor } : { backgroundColor: 'hsl(var(--primary))' }}
              data-testid="blog-website-cta-link"
            >
              Contact / Visit Us <ExternalLink size={14} />
            </a>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-border text-center">
          <Link
            to={`/blog/${slug}`}
            className="text-sm font-medium text-primary hover:underline"
            style={brandColor ? { color: brandColor } : undefined}
          >
            ← Back to {data?.page_name || 'all posts'}
          </Link>
          <p className="text-xs text-muted-foreground mt-3" data-testid="blog-footer-branding">
            Powered by {branding.app_name || 'CredBloom'}
          </p>
        </div>
      </article>
    </div>
  );
}
